"use client";

import { z } from "zod";
import { LoginFormSchema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormMessage } from "@/components/ui/form";
import FormFields from "../Fields";
import { useTranslations } from "next-intl";
import { MdAlternateEmail } from "react-icons/md";
import Buttons from "@/components/core/Buttons";
import hooks from "@/hooks";
import { useToast } from "@/hooks/use-toast";
import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import helpers from "@/helpers";
import { getCookie, setCookie } from "cookies-next";
import PhoneInput from "react-phone-input-2";
import { ptBRCountries } from "@/consts/PtBRCountries";
import { useState } from "react";
import actions from "@/actions";

type Login = z.infer<typeof LoginFormSchema>;

const LoginForm = () => {
  const t = useTranslations("Auth.LoginPage.form");
  const { toast } = useToast();
  const router = useRouter();

  const [loginType, setLoginType] = useState<"email" | "phone">("phone");

  const etapaOnboarding = hooks.useGetOnboarding();

  const form = useForm<Login>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      email: "",
      phone: "",
      password: "",
      loginType: "phone",
    },
  });

  const loginService = hooks.useLogin({
    onSuccess: (data) => {
      setCookie("user_intent", data.userIntent, {
        path: "/",
      });
    },
    onError: (error: unknown) => {
      if ((error as Error).message === "NEXT_REDIRECT") return;
      toast({
        title: "Não foi possível efetuar login",
        description:
          error instanceof AxiosError
            ? error.response?.data?.message
            : (error as Error).message,
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: Login) {
    await loginService.mutateAsync({
      identifier: (data.loginType === "email"
        ? data.email
        : data.phone)!.toLowerCase(),
      password: data.password,
    });

    const { user } = await helpers.userCookieJwtHelper();

    setCookie("user_role", user.role, { path: "/" });

    const intent = getCookie("user_intent");

    const userProfileData = await actions.getUserProfileAction({
      userId: user.userid,
    });

    if (user.role === "ADMIN") {
      router.replace("/mensagens");
      return;
    }

    if (user.role !== "OWNER" && intent === "OWNER") {
      router.replace(`/plans/${user.userid}`);
      return;
    }

    if (
      user.role === "OWNER" &&
      userProfileData.credit_card_token.length === 0
    ) {
      router.replace(`/plans/${user.userid}/Zoe`);
      return;
    }

    const onboarded = user.onboarded;

    if (user.role === "OWNER" && onboarded === false) {
      const result = await etapaOnboarding.mutateAsync(user.userid);

      if (!result) {
        router.replace("/zoe");
        return;
      }

      const hasWorkingHours = result?.salon_working_hours?.length > 0;
      const hasProfessions = result?.salon_professions?.length > 0;

      let etapa = 1;
      if (hasWorkingHours) {
        etapa = 2;
        if (hasProfessions) {
          etapa = 3;
        }
      }

      if (etapa === 1) {
        router.replace(`/onboarding/`);
      } else if (etapa === 2) {
        router.replace(`/onboarding/services`);
      } else {
        router.replace(`/onboarding/team`);
      }

      return;
    }

    router.replace("/zoe");
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="md:p-8 w-full max-w-96 flex flex-col items-center gap-y-6 mb-6 md:mb-0"
      >
        <div className="w-full flex justify-center">
          <div className="relative flex items-center bg-gray-200 rounded-full p-1 w-64 h-10">
            <div
              className={`absolute top-1 left-1 w-[48.5%] h-8 bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out ${
                loginType === "email" ? "translate-x-full" : ""
              }`}
            />
            <button
              type="button"
              onClick={() => {
                setLoginType("phone");
                form.setValue("loginType", "phone");
              }}
              className={`relative z-10 flex-1 text-sm font-medium transition-colors duration-300 ${
                loginType === "phone" ? "text-black" : "text-gray-600"
              }`}
            >
              WhatsApp
            </button>

            <button
              type="button"
              onClick={() => {
                setLoginType("email");
                form.setValue("loginType", "email");
              }}
              className={`relative z-10 flex-1 text-sm font-medium transition-colors duration-300 ${
                loginType === "email" ? "text-black" : "text-gray-600"
              }`}
            >
              Email
            </button>
          </div>
        </div>

        {loginType === "email" ? (
          <FormFields.InputFormField
            form={form}
            formName="email"
            label="Email"
            placeholder={t("placeholder.email")}
            rightElement={<MdAlternateEmail size={16} />}
          />
        ) : (
          <div className="flex flex-col w-full">
            <label htmlFor="whatsapp" className="text-sm font-semibold mb-1">
              {t("label.phone")}
            </label>
            <PhoneInput
              country={"br"}
              value={form.watch("phone")}
              onChange={(value) => {
                const numericValue = value.replace(/\D/g, "");
                form.setValue("phone", numericValue);
              }}
              enableSearch
              countryCodeEditable={false}
              localization={{ ...ptBRCountries }}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderRadius: "9999px",
                border: "1px solid #e5e7eb",
                backgroundColor: "transparent",
                padding: "0.25rem 0.75rem",
                paddingLeft: "55px",
                fontSize: "1rem",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                transition: "all 0.2s",
                outline: "none",
              }}
              containerStyle={{ width: "100%" }}
              buttonStyle={{
                borderTopLeftRadius: "9999px",
                borderBottomLeftRadius: "9999px",
                width: "50px",
                border: "1px solid rgb(229, 231, 235)",
                paddingLeft: "6px",
              }}
              dropdownStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
              inputProps={{
                name: "whatsapp",
                placeholder: t("placeholder.whatsapp"),
              }}
            />
            {form.formState.errors.phone && (
              <FormMessage className="mt-2">
                {form.formState.errors.phone.message}
              </FormMessage>
            )}
          </div>
        )}

        <FormFields.InputFormField
          form={form}
          formName="password"
          label="Senha"
          placeholder={t("placeholder.password")}
          type="password"
        />
        <Buttons.DefaultButton
          title={t("button")}
          type="submit"
          onClick={form.handleSubmit(onSubmit)}
        />
      </form>
    </Form>
  );
};

export default LoginForm;
